import { useMutation } from '@tanstack/react-query';
import { Button, Dialog } from 'components/ui';
import { useOrgUsers } from 'hooks/use-org-users';
import { ApiClient } from 'lib/api/client';
import { ListingRead } from 'lib/api/generated';
import { useStore } from 'lib/store';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PATHS, REDIRECT_URL } from 'settings/config';
import NotifyNetworkComponent from './notify-network-component';

type Props = {
  isOpen: boolean;
  onClose: VoidFunction;
  networks?: string[];
  listing: ListingRead;
};

const NotifyNetworkDialog = ({ isOpen, onClose, networks, listing }: Props) => {
  const { id, creator } = listing;
  const router = useRouter();
  const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);

  const [notifyNetworks, setNotifyNetworks] = useState<string[]>([]);
  const { data: orgUser, isLoading: orgUserLoading } = useOrgUsers({
    status: 'APPROVED',
  });

  const { mutate, isLoading, data } = useMutation(
    () =>
      new ApiClient().listingApi.orgMarketplaceListingNotifyCreate({
        listingId: id,
        buyerOrgId: activeBuyerOrgId as string,
        listingNetworksNotifyRequest: {
          networks: notifyNetworks,
        },
      }),
    {
      onSuccess: () => {
        toast.info('Notifications to networks delivered successfully');
        onClose();
        localStorage.getItem(REDIRECT_URL) &&
        localStorage.getItem(REDIRECT_URL)!.includes('app')
          ? router.push(`${PATHS.app.listing}/${id}`)
          : router.push(`${PATHS.marketplace.viewListings}/${id}`);
      },
      onError: () => {
        toast.error('Failed to push the notifications to networks.');
      },
    }
  );

  const selectedNetworks = useMemo(() => {
    if (!orgUser) return [];
    if (!creator?.user_id) return [];
    const selectedNetwork = orgUser.results?.find(
      (user) => user.user?.user_id === creator?.user_id
    )?.networks;
    return selectedNetwork;
  }, [listing, orgUser, networks]);

  return (
    <Dialog
      title='Notify Networks'
      isOpen={isOpen}
      onDismiss={onClose}
      dismissOnOverlayClick={false}
      actions={
        <>
          <Button
            variant='outline'
            size='medium'
            type='button'
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            Skip
          </Button>
          <Button
            size='medium'
            type='button'
            onClick={async () => {
              await mutate();
            }}
            isLoading={isLoading}
            disabled={notifyNetworks && !notifyNetworks.length}
          >
            Send
          </Button>
        </>
      }
    >
      <NotifyNetworkComponent
        networks={networks}
        setNetworks={setNotifyNetworks}
        selectedNetworks={selectedNetworks}
      />
    </Dialog>
  );
};

export default NotifyNetworkDialog;
